import React, { useState, useEffect, useCallback } from 'react';
import TagChip from './TagChip';
import TagPicker from './TagPicker';
import { applyTag, removeTag, fetchTagsFor } from '../lib/tags';

// Applied-tags strip for a single record (supplier or brief): the label, the
// applied chips (each removable), and a dashed "+ Add tag" trigger that opens
// the TagPicker. Self-loads its tags and handles apply/remove optimistically.
// `variant` is forwarded to each TagChip. Default is 'coloured' so the
// tag-picker / list-filter contexts keep their colour-coded scanning;
// brief detail passes 'muted' to render uniform low-noise chips on the
// content page.
export default function RecordTags({ vocabulary, entityId, actorId, label = 'Tags', pickerAlign = 'left', onApplied, onRemoved, variant = 'coloured' }) {
  const [tags, setTags] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!entityId) return;
    const map = await fetchTagsFor(vocabulary, [entityId]);
    // Sort by name so the chip order is stable and readable. Without
    // this the chips landed in insertion order (or whatever order the
    // backend returned), producing "Test Tag 1, 2, 22, 3, 4" — visually
    // out of order to a human reading natural-numeric.
    const list = map.get(entityId) || [];
    setTags(sortByName(list));
  }, [vocabulary, entityId]);

  useEffect(() => { load(); }, [load]);

  // localeCompare with numeric:true so "Test Tag 22" sorts after
  // "Test Tag 3" rather than between "2" and "3" (lexicographic
  // ordering trips humans up the moment a tag name contains a number).
  const sortByName = (list) => [...list].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));

  const handleApply = async (tag) => {
    if (tags.some(t => t.id === tag.id)) return;
    setTags(prev => sortByName([...prev, tag]));
    const { error } = await applyTag(vocabulary, entityId, tag.id, actorId);
    if (error) {
      setTags(prev => prev.filter(t => t.id !== tag.id));
      return;
    }
    // Success — let the parent know so it can fire a toast (or any other
    // side effect). Optional; component still works without a handler.
    if (typeof onApplied === 'function') onApplied(tag);
  };

  const handleRemove = async (tag) => {
    setTags(prev => prev.filter(t => t.id !== tag.id));
    const { error } = await removeTag(vocabulary, entityId, tag.id);
    if (error) {
      setTags(prev => sortByName([...prev, tag]));
      return;
    }
    if (typeof onRemoved === 'function') onRemoved(tag);
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-2 flex-wrap">
        {label && <span className="fusion-field-label">{label}</span>}
        {tags.map(t => <TagChip key={t.id} tag={t} variant={variant} onRemove={() => handleRemove(t)} />)}
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 border border-dashed border-line text-[#51534A] hover:text-fusion-mid-grey hover:border-[#DADCCD] text-[11px] font-bold font-['Metropolis'] transition-colors"
        >
          + Add tag
        </button>
      </div>
      {open && (
        <TagPicker
          vocabulary={vocabulary}
          appliedTags={tags}
          actorId={actorId}
          align={pickerAlign}
          onApply={handleApply}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
